



















import { MediaItem } from '@/api/models';
import { SiteState } from '@/store/types';
import { Component, Prop, Vue } from "vue-property-decorator";
import { State } from 'vuex-class';
import { VimePlayer, VimeVideo, VimeDefaultUi, VimeVimeo, VimeDailymotion, VimeYoutube, VimeDash } from '@vime/vue';
import { notEmptyString } from '@/api/validators';
import { expandPath } from '@/api/helpers';
import { mediaIdMatchPatterns } from '@/api/media-providers';

declare global {
    interface Window {
        Twitch: any;
        FB: any;
    }
}
@Component({
  name: "MultiMediaPlayer",
  components: {
    VimePlayer, VimeVideo, VimeDefaultUi,VimeVimeo, VimeDailymotion, VimeYoutube, VimeDash,
  },
  filters: {},
})
export default class MultiMediaPlayer extends Vue {
  @Prop({ default: () => new MediaItem() }) mediaItem: MediaItem;
  @State("site") site: SiteState;

  scriptLoaded = false;

  useIframe = false;

  useDiv = false;

  src = "";

  extraClasses = [];

  allowStringParams = "";
 
 mounted() {
   this.sync();
 }

  sync() {
    this.useDiv = false;
    this.useIframe = false;
    this.src = "";
    setTimeout(()=>{
     switch (this.provider) {
       case 'twitch':
         this.useDiv = true;
         this.loadTwitch();
         break;
        case 'sketchfab':
         this.useIframe = true;
         this.loadSketchfab();
         break;
        case "issuu":
          this.useIframe = true;
          this.loadIssuu();
          break;
        case "shapediver":
          this.useIframe = true;
          this.loadShapediver();
          break;
        case "adobe":
        case "indesign":
          this.useIframe = true;
          this.loadInDesign();
          break;
        case "facebook":
          this.useIframe = true;
          this.loadFacebook();
          break;
        case "soundcloud":
          this.useIframe = true;
          this.loadSoundcloud();
          break;
        case "spotify":
          this.useIframe = true;
          this.loadSpotify();
          break;
        case "sirv":
          this.useDiv = true;
          this.loadSirv();
          break;
     }
   }, 250);
  }
  vimeProviders =[
    "youtube",
    "vimeo",
    "dailymotion",
    "dash"
  ];

  get provider() {
    return this.mediaItem.provider;
  }

  get useVime() {
    return this.vimeProviders.includes(this.provider) || this.isDirectVideo;
  }

  get source() {
    return notEmptyString(this.mediaItem.source, 2)? this.mediaItem.source : 'local';
  }

  get isDirectVideo() {
    return (this.mediaItem.type === 'video' || this.mediaItem.mime.startsWith('video')) && ['cloudinary','local'].includes(this.source);
  }

  get altRef() {
    return notEmptyString(this.mediaItem.title)? this.mediaItem.title : this.provider;
  }

  get dataSrc() {
    switch (this.provider) {
      case 'sirv':
        return this.filename;
      default:
        return '';
    }
  }

  get hasOtherProvider() {
    return notEmptyString(this.mediaItem.filename,8) && !this.useVime;
  }

  get filename() {
    return notEmptyString(this.mediaItem.filename,8)? this.mediaItem.filename : "";
  }

  get filePath() {
    const isCloudinary = /res\.cloudinary/.test(this.filename);
    return isCloudinary? this.filename : expandPath(this.filename);
  }

  get objectId() {
    let objId = "";
    const keys = Object.keys(mediaIdMatchPatterns)
    if (keys.includes(this.provider)) {
      const regexes = mediaIdMatchPatterns[this.provider];
      if (regexes instanceof Array) {
        for (const rgx of regexes) {
        const m = this.mediaItem.filename.match(rgx);
          if (m) {
            objId = m[1];
          }
        }
      }
    } else {
      const end = this.filename.split('/').pop();
      objId = end.replace(/[&?]/,'-');
    }
    if (notEmptyString(objId)) {
      return objId.trim();
    } else {
      return "";
    }
  }

  get aspectRatio() {
    switch (this.provider) {
      case 'spotify':
        return 4 / 1;
      default:
        return 9 / 16;
    }
  }

  loadRemote(url: string) {
    const scriptId = [this.provider,'sdk'].join('-');
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("src", url);
      script.setAttribute("id", scriptId);
      script.setAttribute("type","text/javascript");
      document.body.append(script);
    }
    this.scriptLoaded = true;
  }

  loadTwitch() {
    const url = 'https://player.twitch.tv/js/embed/v1.js';
    this.loadRemote(url);
    setTimeout(() => {
      if (window.Twitch instanceof Object && notEmptyString(this.objectId, 3)) {
        const player = new window.Twitch.Player(this.objectId, {
          video: this.objectId,
          channel: '',
          height: '100%',
          width: '100%',
          playsinline: true,
          autoplay: false,
          muted: false,
          controls: true,
        });
        if (player) {
          this.extraClasses = ['twitch'];
        }
      }
    }, 500);
  }

  loadSirv() {
    const url = 'https://scripts.sirv.com/sirv.js';
    this.loadRemote(url);
    this.extraClasses = ['Sirv'];
  }


  loadIssuu() {
   const uriParts = this.filename.split('/');
   const docsIndex = uriParts.indexOf('docs');
   const docNameIndex = docsIndex + 1;
   if (uriParts.length > docNameIndex) {
     const userName = uriParts[docsIndex - 1];
     const docName = uriParts[docNameIndex];
     this.allowStringParams = "fullscreen";
     this.src = `//e.issuu.com/embed.html?d=${docName}&u=${userName}`;
   }
  }

  loadSpotify() {
    this.src = this.spotifyUri;
  }

  get spotifyUri() {
    const variants = [
      { base: "spotify:track",
        type: "split",
        target: `open.spotify.com/embed/track`
      },
      { base: "spotify:album",
        type: "split",
        target: `open.spotify.com/embed/album`
      },
      { base: "spotify:show",
        type: "split",
        target: `open.spotify.com/embed-podcast/show`
      },
      { base: "spotify:episode",
        type: "split",
        target: `open.spotify.com/embed-podcast/episode`
      },
      { base: "spotify:playlist",
        type: "split",
        target: `open.spotify.com/embed/playlist`
      },
      { base: "open.spotify.com/track/",
        type: "replace",
        find: "open.spotify.com/",
        target: "open.spotify.com/embed/",
      },
      {
        base: "open.spotify.com/album/",
        type: "replace",
        find: "open.spotify.com/",
        target: "open.spotify.com/embed/"
      },
      { base: "open.spotify.com/show/",
        type: "replace",
        find: "open.spotify.com/",
        target: "open.spotify.com/embed-podcast/"
      },
      { base: "open.spotify.com/episode/",
        type: "replace",
        find: "open.spotify.com/",
        target: "open.spotify.com/embed-podcast/"
      },
      { base: "open.spotify.com/playlist/",
        type: "replace",
        find: "open.spotify.com/",
        target: "open.spotify.com/embed/"
      }
    ];
    const variant = variants.find(v => this.filename.includes(v.base));
    if (variant instanceof Object) {
      if (variant.type === "replace") {
        return this.filename.replace(variant.find, variant.target);
      } else {
        const id = this.filename.split(":").pop();
        return ['https:/', variant.target, id].join('/');
      }
    }
    return this.filename;
  }

  get encodedUrl() {
    return encodeURIComponent(this.mediaItem.filename);
  }


  loadFacebook() {
    this.allowStringParams = "autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share";
    this.src = `https://www.facebook.com/plugins/video.php?href=${this.encodedUrl}&show_text=0&width=560`;
  }

  loadSoundcloud() {
    this.src = `https://w.soundcloud.com/player/?url=${this.encodedUrl}&visual=true&show_artwork=true&maxwidth=810&maxheight=1000&dnt=1`;
  }

  loadSketchfab() {
    this.src = `${this.filename}/embed?preload=1&amp;ui_controls=0&amp;ui_infos=0&amp;ui_inspector=0&amp;ui_stop=0&amp;ui_watermark=1&amp;ui_watermark_link=0`;
  }

  loadShapediver() {
    this.src = `${this.filename}&brandedMode=false`;
  }

  loadInDesign() {
    this.allowStringParams = "fullscreen";
    this.src = this.filename;
  }

  get wrapperClasses() {
    const cls = [this.mediaItem.type, this.provider];
    if (this.provider === 'spotify') {
      const { filename } = this.mediaItem;
      const spSchema = filename.startsWith("spotify:");
      const sep = spSchema ? "spotify:" : '.com/';
      const sep2 = spSchema ? ':' : '/';
      const suffix = filename.split(sep).pop().split(sep2).shift();
      cls.push(['spotify', suffix].join('-'));
    }
    return cls;
  }

}
